import { Accordion, Button } from "flowbite-react";
import { useState, useEffect } from 'react';
import BasicAtsTemplate from "../templates/BasicAtsTemplate";
import useEditorController from '../controllers/useEditorController';
import { useSession } from '../context/SessionContext';
import { useNavigate, useParams } from 'react-router-dom';
import PDFViewerCustom from '../components/PDFViewerCustom';

const Editor = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isInitialized, setIsInitialized] = useState(false);
  const [cvId, setCvId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const controller = useEditorController();
  
  const {
    profile,
    experiences,
    educations,
    certificates,
    publications,
    skills,
    achievements,
    portfolios,
    error,
    selectedItems,
    toggleItem,
    toggleProfile,
    saveCV,
    fetchCV,
    updateCV,
    setSelectedItems
  } = controller;

  const [tempProfile, setTempProfile] = useState({
    title: '',
    summary: '',
    show_avatar: false,
    show_email: false,
    show_github: false,
    show_linkedin: false,
    show_behance: false,
    show_instagram: false,
    show_youtube: false,
    show_personal_web: false,
    show_phone: false
  });
  
  const [localProfile, setLocalProfile] = useState({
    title: '',
    summary: '',
    show_avatar: false,
    avatarUrl: '',
    show_email: false,
    show_github: false,
    show_linkedin: false,
    show_behance: false,
    show_instagram: false,
    show_youtube: false,
    show_personal_web: false,
    show_phone: false
  });

  useEffect(() => {
    if (session) {
      setIsInitialized(true);
      
      setLocalProfile(prev => ({
        ...prev,
        title: profile.title_web || '',
        summary: profile.bio_web || '',
        avatarUrl: profile.avatarUrl || '',
        show_avatar: profile.show_avatar || false,
        show_email: profile.show_email || false,
        show_github: profile.show_github || false,
        show_linkedin: profile.show_linkedin || false,
        show_behance: profile.show_behance || false,
        show_instagram: profile.show_instagram || false,
        show_youtube: profile.show_youtube || false,
        show_personal_web: profile.show_personal_web || false,
        show_phone: profile.show_phone || false
      }));

      setTempProfile(prev => ({
        ...prev,
        title: profile.title_web || '',
        summary: profile.bio_web || '',
        show_avatar: profile.show_avatar || false,
        show_email: profile.show_email || false,
        show_github: profile.show_github || false,
        show_linkedin: profile.show_linkedin || false,
        show_behance: profile.show_behance || false,
        show_instagram: profile.show_instagram || false,
        show_youtube: profile.show_youtube || false,
        show_personal_web: profile.show_personal_web || false,
        show_phone: profile.show_phone || false
      }));

      if (id) {
        const fetchUserCV = async () => {
          try {
            const cv = await fetchCV(id);
            if (!cv) {
              navigate('/dashboard');
              return;
            }
            
            setCvId(cv.id);
            setSelectedItems({
              profile: !!cv.cv_data?.profile,
              experiences: cv.cv_data?.experiences?.map(e => e.id) || [],
              educations: cv.cv_data?.educations?.map(e => e.id) || [],
              certificates: cv.cv_data?.certificates?.map(c => c.id) || [],
              publications: cv.cv_data?.publications?.map(p => p.id) || [],
              skills: cv.cv_data?.skills?.map(s => s.id) || [],
              achievements: cv.cv_data?.achievements?.map(a => a.id) || [],
              portfolios: cv.cv_data?.portfolios?.map(p => p.id) || []
            });

            if (cv.cv_data?.profile) {
              setLocalProfile(prev => ({
                ...prev,
                ...cv.cv_data.profile,
                avatarUrl: profile.avatarUrl
              }));
              setTempProfile(prev => ({
                ...prev,
                ...cv.cv_data.profile
              }));
            }
          } catch (error) {
            console.error("Error initializing CV:", error);
          }
        };
        fetchUserCV();
      }
    }
  }, [id, session]);

  const handleTempProfileChange = (e) => {
    const { name, type, value, checked } = e.target;
    setTempProfile(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateProfile = () => {
    const updatedProfile = {
      ...tempProfile,
      avatarUrl: profile.avatarUrl
    };
    setLocalProfile(updatedProfile);
  };

  const handleSaveCV = async () => {
    try {
      const cvData = {
        profile: selectedItems.profile ? {
          ...localProfile,
          avatarUrl: profile.avatarUrl
        } : null,
        experiences: experiences?.filter(e => selectedItems.experiences.includes(e.id)),
        educations: educations?.filter(e => selectedItems.educations.includes(e.id)),
        certificates: certificates?.filter(c => selectedItems.certificates.includes(c.id)),
        publications: publications?.filter(p => selectedItems.publications.includes(p.id)),
        skills: skills?.filter(s => selectedItems.skills.includes(s.id)),
        achievements: achievements?.filter(a => selectedItems.achievements.includes(a.id)),
        portfolios: portfolios?.filter(p => selectedItems.portfolios.includes(p.id))
      };
      
      const template = 'basic_ats';
      
      if (cvId) {
        await updateCV(cvId, cvData, template);
      } else {
        const savedCV = await saveCV(cvData, template);
        if (savedCV) setCvId(savedCV.id);
      }
      alert('CV saved successfully!');
    } catch (error) {
      console.error("Error saving CV:", error);
      alert('Error saving CV');
    }
  };

  const EditorCard = ({ title, description, isSelected, onToggle, sectionKey }) => (
    <div className="flex justify-between items-center p-3 mb-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow w-full">
      <div className="min-w-0 pr-2">
        <h3 className="text-sm font-semibold text-gray-800 truncate">{title}</h3>
        {!['skills', 'portfolios', 'achievements'].includes(sectionKey) && (
          <p className="text-xs text-gray-500 truncate">{description}</p>
        )}
      </div>
      <Button
        size="xs" 
        color={isSelected ? "failure" : "light"}
        onClick={onToggle}
        className="shrink-0"
      >
        {isSelected ? "Remove" : "Add"}
      </Button>
    </div>
  );

  const renderSection = (title, items, sectionKey) => {
    if (!isInitialized) return <p>Loading...</p>;
    
    return (
      <Accordion.Panel>
        <Accordion.Title className="focus:ring-0">
          {title}
        </Accordion.Title>
        <Accordion.Content className="overflow-x-auto">
          <div className="space-y-2">
            {error ? (
              <p>Error: {error}</p>
            ) : items?.length > 0 ? (
              items.map((item) => (
                <EditorCard
                  key={item.id}
                  sectionKey={sectionKey}
                  title={item.title || item.position || item.name}
                  description={item.description || item.institution || item.issuer}
                  isSelected={selectedItems[sectionKey]?.includes(item.id)}
                  onToggle={() => toggleItem(sectionKey, item.id)}
                />
              ))
            ) : (
              <p className="text-gray-500 text-sm">No {title.toLowerCase()} to display</p>
            )}
          </div>
        </Accordion.Content>
      </Accordion.Panel>
    );
  };

  if (!session) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Loading session...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row h-screen">
      {/* Mobile Header */}
      <div className="md:hidden p-3 border-b flex justify-between items-center bg-white sticky top-0 z-40">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-1 text-gray-600 hover:text-gray-800"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h1 className="text-base font-semibold">CV Editor</h1>
      </div>
  
      {/* Sidebar */}
      <div 
        className={`w-full md:w-80 bg-gray-50 border-r border-gray-200 flex flex-col fixed md:static inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:translate-x-0
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Tambah Header Mobile di Dalam Sidebar */}
        <div className="md:hidden p-3 border-b flex justify-between items-center bg-white">
          <h2 className="text-lg font-semibold">Menu</h2>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="p-1 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>      
        <div className="flex-1 overflow-y-auto p-3">
          <Accordion>
            <Accordion.Panel>
              <Accordion.Title className="focus:ring-0">
                Profile
              </Accordion.Title>
              <Accordion.Content>
                <EditorCard
                  title="Include Profile"
                  description="Basic personal information"
                  isSelected={selectedItems.profile}
                  onToggle={toggleProfile}
                />
                {selectedItems.profile && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Title</label>
                      <input
                        name="title"
                        value={tempProfile.title}
                        onChange={handleTempProfileChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="e.g., Software Developer"
                      />
                    </div>
                  
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Summary Bio</label>
                      <textarea
                        name="summary"
                        value={tempProfile.summary}
                        onChange={handleTempProfileChange}
                        rows="3"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Write a brief summary about yourself"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Photo Settings</label>
                      {profile.avatarUrl && (
                        <img 
                          src={profile.avatarUrl} 
                          className="w-20 h-20 rounded-full mb-2 object-cover border border-gray-200" 
                          alt="Profile" 
                        />
                      )}
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="show_avatar"
                          checked={tempProfile.show_avatar}
                          onChange={handleTempProfileChange}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm">Show Profile Photo</span>
                      </div>
                    </div>
                  
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Social Accounts</label>
                      {['phone', 'email', 'github', 'linkedin', 'behance', 'instagram', 'youtube', 'personal_web'].map((platform) => (
                        profile[platform] && (
                          <div key={platform} className="flex items-center gap-2 min-w-0">
                            <input
                              type="checkbox"
                              name={`show_${platform}`}
                              checked={tempProfile[`show_${platform}`]}
                              onChange={handleTempProfileChange}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm truncate min-w-0">
                              {platform === 'personal_web' ? 'Website' : platform.charAt(0).toUpperCase() + platform.slice(1)}: 
                              {profile[platform] || `No ${platform} in profile`}
                            </span>
                          </div>
                        )
                      ))}
                    </div>

                    <Button onClick={handleUpdateProfile} color="blue" size="sm" className="w-full">
                      Update Profile
                    </Button>
                  </div>
                )}
              </Accordion.Content>
            </Accordion.Panel>

            {renderSection("Experience", experiences, "experiences")}
            {renderSection("Projects", portfolios, "portfolios")}
            {renderSection("Education", educations, "educations")}
            {renderSection("Skills", skills, "skills")}
            {renderSection("Achievements", achievements, "achievements")}
            {renderSection("Certificates", certificates, "certificates")}
            {renderSection("Publications", publications, "publications")}
          </Accordion>
        </div>
        <div className="p-3 border-t border-gray-200 flex gap-2 bg-white">
          <Button onClick={handleSaveCV} color="blue" size="sm" className="flex-1">
            {cvId ? 'Update CV' : 'Save CV'}
          </Button>
          <Button onClick={() => navigate('/dashboard')} color="gray" size="sm">
            Back
          </Button>
        </div>
      </div>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 bg-gray-100 p-3 relative md:static">
        <div className="h-full rounded-lg shadow-sm overflow-hidden flex items-center justify-center">
          <PDFViewerCustom
            // className="min-h-[calc(100vh-6rem)] md:min-h-screen"
            cvData={{
              profileData: selectedItems.profile ? {
                ...localProfile,
                show_avatar: localProfile.show_avatar,
                avatarUrl: profile.avatarUrl,
                fullname: profile.fullname || "Add Data",
                phone: profile.phone,
                email: profile.email,
                socialLinks: {
                  email: localProfile.show_email ? profile.email : null,
                  github: localProfile.show_github ? profile.github : null,
                  linkedin: localProfile.show_linkedin ? profile.linkedin : null,
                  behance: localProfile.show_behance ? profile.behance : null,
                  instagram: localProfile.show_instagram ? profile.instagram : null,
                  youtube: localProfile.show_youtube ? profile.youtube : null,
                  personal_web: localProfile.show_personal_web ? profile.personal_web : null,
                  phone: localProfile.show_phone ? profile.phone : null,
                }
              } : {
                fullname: "Add your data ...",
                title: "",
                summary: "",
              },
              experienceData: experiences?.filter(e => selectedItems.experiences.includes(e.id)),
              educationData: educations?.filter(e => selectedItems.educations.includes(e.id)),
              certificateData: certificates?.filter(c => selectedItems.certificates.includes(c.id)),
              publicationData: publications?.filter(p => selectedItems.publications.includes(p.id)),
              skillData: skills?.filter(s => selectedItems.skills.includes(s.id)),
              achievementData: achievements?.filter(a => selectedItems.achievements.includes(a.id)),
              portfolioData: portfolios?.filter(p => selectedItems.portfolios.includes(p.id))
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Editor;